import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { selectDealerSelected } from 'app/pages/Header/selectors';
import { hasDealerContext } from 'utils/hasDealerContext';
import { hasDealerLaunchDate, dealerHasLaunchDateField } from 'utils/dealerLaunchDate';
import DataGridTypes from 'types/DataGridTypes';

import { useNurturingPlanExecutionFilterTray } from './useNurturingPlanExecutionFilterTray';

export type NurturingPlanExecutionBreadcrumb = {
  label: string;
  link?: string;
  isActive?: boolean;
};

export const useNurturingPlanExecutionPage = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected);
  const [isTestMode, setIsTestMode] = useState(false);
  const [isQuickFilterOpen, setIsQuickFilterOpen] = useState(false);

  const breadcrumbsList = useMemo((): NurturingPlanExecutionBreadcrumb[] => {
    const fromState = (
      location.state as {
        breadcrumbsList?: NurturingPlanExecutionBreadcrumb[];
      } | null
    )?.breadcrumbsList;
    if (fromState?.length) {
      return fromState;
    }
    return [{ label: 'Nurturing Plan Execution', isActive: true }];
  }, [location.state]);

  const hasDealer = hasDealerContext(selectedDealer);
  const hasLaunchDate = hasDealerLaunchDate(selectedDealer);
  const dealerObj =
    typeof selectedDealer === 'object' && selectedDealer !== null
      ? (selectedDealer as { site_title?: string; dealer?: string; _id?: string })
      : null;
  const dealerTitle = dealerObj?.site_title;
  const dealerId = dealerObj?.dealer || dealerObj?._id;

  const handleGridMetaChange = useCallback((meta: Record<string, unknown>) => {
    const nextIsTestMode = Boolean(meta?.is_test_mode);
    setIsTestMode(prev => (prev === nextIsTestMode ? prev : nextIsTestMode));
  }, []);

  const filterTray = useNurturingPlanExecutionFilterTray({
    dealerId: dealerId ? String(dealerId) : '',
    fetchEnabled: isQuickFilterOpen,
  });

  const executionsGridType = useMemo(() => {
    if (!dealerId) return null;
    return DataGridTypes.NURTURING_PLAN_EXECUTIONS(String(dealerId), {
      isTestMode,
      executionFilterModel: filterTray.executionFilterModel,
    });
  }, [dealerId, isTestMode, filterTray.executionFilterModel]);

  useEffect(() => {
    if (hasDealer && (!dealerHasLaunchDateField(selectedDealer) || !hasLaunchDate)) {
      history.replace('/monitor');
    }
  }, [hasDealer, hasLaunchDate, history, selectedDealer]);

  return {
    breadcrumbsList,
    hasDealer,
    hasLaunchDate,
    dealerTitle,
    dealerId,
    executionsGridType,
    isTestMode,
    handleGridMetaChange,
    isQuickFilterOpen,
    setIsQuickFilterOpen,
    filterTray,
  };
};
